import { concat } from '@ember/helper';
import * as v from 'valibot';
import { trackedObject } from '@ember/reactive/collections';
import Component from '@glimmer/component';
import { divide } from 'tio-ui/math';
import errorsForField from '../../../../../helpers/errors-for-field.ts';
import { Input } from 'tio-ui/components/forms';
import inputmask from '../../../../../modifiers/inputmask.ts';
import programLabelFor from '../../../../../helpers/program-label-for.ts';
import type TasApplication from '../../../../../models/tas-application.ts';
import type Owner from '@ember/owner';
import { Button } from 'tio-ui/components/buttons';
import { on } from '@ember/modifier';
import { PhTrash } from 'ember-phosphor-icons';

const validationSchema = v.object({
  scholarshipName: v.pipe(v.string(), v.nonEmpty('Name is required')),
  scholarshipAmount: v.pipe(v.number(), v.minValue(0.01, 'Amount must be greater than $0')),
});

type Scholarship = {
  scholarshipAmount: number;
  scholarshipName: string;
};

interface TuitionAssistanceProgramDetailsAppDetailsEditScholarshipsEditableRowSignature {
  Args: {
    application: TasApplication;
    onRemove: () => void;
    onUpdate: (arg0: Scholarship) => void;
    scholarship: Scholarship;
  };
}

export default class TuitionAssistanceProgramDetailsAppDetailsEditScholarshipsEditableRowComponent extends Component<TuitionAssistanceProgramDetailsAppDetailsEditScholarshipsEditableRowSignature> {
  scholarship;

  constructor(
    owner: Owner,
    args: TuitionAssistanceProgramDetailsAppDetailsEditScholarshipsEditableRowSignature['Args']
  ) {
    super(owner, args);
    this.scholarship = trackedObject(this.args.scholarship);
  }

  updateScholarshipName = (value: string) => {
    this.scholarship.scholarshipName = value;
    this.didUpdateValue();
  };

  updateScholarshipAmount = (_value: unknown, event?: InputEvent) => {
    if (!event) return;
    // @ts-expect-error: inputmask is not a valid property on EventTarget
    const unmaskedValue = event.target.inputmask.unmaskedvalue();
    this.scholarship.scholarshipAmount = Math.round(unmaskedValue * 100);
    this.didUpdateValue();
  };

  didUpdateValue = () => {
    this.args.onUpdate?.(this.scholarship);
  };

  get formValidationErrors() {
    const result = v.safeParse(validationSchema, this.scholarship);

    if (!result.success) {
      const flattened = v.flatten(result.issues);
      const nested = flattened.nested ?? {};

      // Convert Valibot errors to format compatible with errorsForField helper (Yup format)
      return Object.entries(nested).map(([field, messages]) => ({
        path: field,
        errors: messages ?? [],
        value: this.scholarship[field as keyof typeof this.scholarship] as string,
      }));
    }

    return [];
  }

  <template>
    <div class="flex justify-center items-center">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 my-8 w-full">
        <div>
          <div class="font-semibold text-sm text-tio-gray-700">
            {{programLabelFor
              "SCHOLARSHIP_NAME"
              programTemplate=@application.tasProgramInstance.tasProgramTemplate
            }}
            <span class="text-error-400">*</span>
          </div>
          <Input
            @value={{@scholarship.scholarshipName}}
            @onInput={{this.updateScholarshipName}}
            @errors={{errorsForField
              (concat "scholarshipName")
              schemaErrors=this.formValidationErrors
            }}
          />
        </div>

        <div>
          <div class="font-semibold text-sm text-tio-gray-700">
            {{programLabelFor
              "SCHOLARSHIP_AMOUNT"
              programTemplate=@application.tasProgramInstance.tasProgramTemplate
            }}
            <span class="text-error-400">*</span>
          </div>
          <Input
            value={{divide @scholarship.scholarshipAmount 100}}
            @onInput={{this.updateScholarshipAmount}}
            @errors={{errorsForField
              (concat "scholarshipAmount")
              schemaErrors=this.formValidationErrors
            }}
            {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
          />
        </div>
      </div>

      <div class="self-center mt-4">
        <Button @appearance="outlined" @intent="danger" {{on "click" @onRemove}}>
          <PhTrash @size="20" />
        </Button>
      </div>
    </div>
  </template>
}
